import { useEffect, useState } from 'react';
import { ErrorNotice, Spinner } from '../ui.tsx';
import { ApiRequestError } from '../../lib/api.ts';

/**
 * The pieces every workspace settings tab needs.
 *
 * These tabs live under Management — they configure the workspace, which is a
 * manager's job, not a personal preference. Settings keeps only the things that
 * belong to the person signed in. The payload is still one `/settings` call, so
 * the shape is shared rather than split along the same line.
 */

export interface SettingsDto {
  profile: { id: number | null; displayName: string; avatar: string | null; email: string; role: string };
  preferences: { sendWithEnter: boolean; soundEnabled: boolean; soundVolume: number; theme: string };
  chat: {
    uuid: string;
    title: string;
    integration: {
      showChatIcon: boolean;
      askEmail: boolean;
      askPhone: boolean;
      widgetColor: string;
    };
    archiving?: { days: number; ongoingChats: boolean; newChats: boolean };
    notifications?: { agentsWhenOffline: boolean; capturedForms: boolean; unanswered: boolean };
    craigslist?: { enabled: boolean; username: string; domain: string };
    twilio?: { configured: boolean; phoneNumber: string; accountSid: string };
  };
  widget: { snippet: string };
}

export function useSaved(): [boolean, () => void] {
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!saved) return;

    const timer = window.setTimeout(() => {
      setSaved(false);
    }, 2500);

    return () => {
      window.clearTimeout(timer);
    };
  }, [saved]);

  return [
    saved,
    () => {
      setSaved(true);
    },
  ];
}

export function SaveRow({
  pending,
  saved,
  onSave,
  error,
  /** Blocks the save outright, for input the server would reject anyway. */
  disabled = false,
}: {
  pending: boolean;
  saved: boolean;
  onSave: () => void;
  error?: string | null;
  disabled?: boolean;
}): React.JSX.Element {
  return (
    <>
      {error ? (
        <div className="mt-3">
          <ErrorNotice message={error} />
        </div>
      ) : null}
      <div className="mt-4 flex items-center justify-end gap-3">
        {saved ? <span className="text-xs text-emerald-600 dark:text-emerald-400">Saved</span> : null}
        <button
          type="button"
          className="lg-btn-primary"
          disabled={pending || disabled}
          onClick={onSave}
        >
          {pending ? <Spinner /> : null}
          Save changes
        </button>
      </div>
    </>
  );
}

export function saveError(error: unknown): string | null {
  if (error instanceof ApiRequestError) return error.message;
  return error ? 'Could not save. Try again.' : null;
}

export function saveFailureMessage(caught: unknown): string {
  if (!(caught instanceof ApiRequestError)) return 'Something went wrong';

  const fields = Object.values(caught.fieldErrors);

  return fields.length > 0 ? fields.join('. ') : caught.message;
}
